import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';

import { AppPage } from '@/shared/components';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import PageHeader from '@/shared/components/PageHeader';
import { cx } from '@/styles/muiTheme';
import { getBusinessApiEndPoint } from '@/util/configureHttp';

import useMcpToolsQuery from '../hooks/useMcpToolsQuery';

// Not formatEndpoint: it appends the user's idToken, and this URL is meant to be
// pasted into an agent's config.
const MCP_SERVER_URL = `${getBusinessApiEndPoint()}/v1.0/mcp`;

const claudeCodeCommand = `claude mcp add --transport http c360 ${MCP_SERVER_URL}`;

const mcpJsonConfig = `{
  "mcpServers": {
    "c360": {
      "type": "http",
      "url": "${MCP_SERVER_URL}"
    }
  }
}`;

const CodeBlock = ({ children }: { children: string }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 2,
      borderRadius: 1,
      overflowX: 'auto',
      fontSize: '0.8rem',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    }}
  >
    <code>{children}</code>
  </Box>
);

const McpPage = () => {
  const title = 'MCP Server';
  const toolsQuery = useMcpToolsQuery();

  useDocumentTitle(title);

  return (
    <AppPage title={title} requiredPermissions={['menu:mcp']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader
          title={title}
          subtitle="Let an AI agent find games and players and create clips on your behalf."
        />
        <Card sx={{ marginTop: 4 }}>
          <CardContent>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="h6">Connect your agent</Typography>
                <Typography variant="body2">
                  Add this server to your agent. On first use it opens a sign-in page: log in
                  with your account and pick the organization the agent works in. The agent
                  acts as you.
                </Typography>
                <Typography variant="body2">
                  Claude (web and Desktop): Settings → Connectors → Add custom connector, with
                  this URL:
                </Typography>
                <CodeBlock>{MCP_SERVER_URL}</CodeBlock>
                <Typography variant="body2">Claude Code (then run /mcp to sign in):</Typography>
                <CodeBlock>{claudeCodeCommand}</CodeBlock>
                <Typography variant="body2">Other MCP clients (.mcp.json):</Typography>
                <CodeBlock>{mcpJsonConfig}</CodeBlock>
                <Typography variant="body2">
                  A client that can&apos;t sign in can send an API key as an X-API-Key header
                  instead. Create one on the <Link to="/api-key">API Key</Link> page.
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="h6">Tools</Typography>
                {toolsQuery.isLoading && <CircularProgress size={16} />}
                {toolsQuery.isError && (
                  <Typography variant="body2" sx={{ color: cx.red.main }}>
                    Could not load the tools.
                  </Typography>
                )}
                <Box
                  sx={{
                    // Hovering one tool fades the rest. The rows have padding rather
                    // than gaps between them, so moving between tools never lands on
                    // a gap and flashes them all back.
                    '& > *': { transition: 'opacity 150ms' },
                    '&:has(> :hover) > :not(:hover)': { opacity: 0.35 },
                  }}
                >
                  {toolsQuery.data?.map(({ name, description }) => (
                    <Stack key={name} spacing={0.75} sx={{ alignItems: 'flex-start', py: 1 }}>
                      <Chip
                        size="small"
                        label={name}
                        sx={{ fontFamily: cx.fontMono, color: orange[500] }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: cx.fontMono, fontSize: '0.8rem' }}
                      >
                        {description}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </AppPage>
  );
};

export default McpPage;
